import { useState, useEffect } from 'react';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';

export default function MessageLogs() {
  const { selectedBusiness } = useAuth();
  const bid = selectedBusiness?.id;
  const [logs, setLogs] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [status, setStatus] = useState('All');
  const [type, setType] = useState('All');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const perPage = 50;

  useEffect(() => {
    if (!bid) return;
    setLoading(true);
    api.get(`/messages?business_id=${bid}&limit=500`)
      .then(r => {
        const data = Array.isArray(r.data) ? r.data : (r.data?.messages || []);
        setLogs(data);
        setFiltered(data);
        setLoading(false);
      }).catch(() => setLoading(false));
  }, [bid]);

  useEffect(() => {
    let data = [...logs];
    if (status !== 'All') data = data.filter(m => m.status === status.toLowerCase());
    if (type !== 'All') data = data.filter(m => (m.type || 'text') === type.toLowerCase());
    if (phone) data = data.filter(m => m.phone?.includes(phone));
    setFiltered(data);
    setPage(1);
  }, [status, type, phone, logs]);

  const paginated = filtered.slice((page - 1) * perPage, page * perPage);
  const totalPages = Math.ceil(filtered.length / perPage);

  const statusColor = (s) => {
    if (s === 'sent') return { background: '#dcfce7', color: '#166534' };
    if (s === 'delivered') return { background: '#dbeafe', color: '#1d4ed8' };
    if (s === 'read') return { background: '#ede9fe', color: '#6d28d9' };
    if (s === 'failed') return { background: '#fee2e2', color: '#dc2626' };
    return { background: '#f3f4f6', color: '#6b7280' };
  };

  const S = {
    page: { fontFamily: 'DM Sans, sans-serif', background: '#1a1a2e', minHeight: '100vh', color: '#e5e7eb' },
    topBar: { background: '#16213e', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 24, borderBottom: '1px solid #0f3460' },
    navLink: { fontSize: 15, color: '#9ca3af', cursor: 'pointer', fontWeight: 500 },
    title: { fontSize: 18, fontWeight: 700, color: '#fff', padding: '20px 24px 12px' },
    filterRow: { display: 'flex', gap: 12, padding: '0 24px 16px', alignItems: 'center', flexWrap: 'wrap' },
    label: { fontSize: 14, color: '#9ca3af', marginRight: 6 },
    select: { padding: '7px 12px', background: '#16213e', border: '1px solid #0f3460', borderRadius: 6, color: '#e5e7eb', fontSize: 13 },
    input: { padding: '7px 12px', background: '#16213e', border: '1px solid #0f3460', borderRadius: 6, color: '#e5e7eb', fontSize: 15, width: 160 },
    filterBtn: { background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, padding: '7px 16px', fontSize: 15, fontWeight: 600, cursor: 'pointer' },
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: '10px 16px', fontSize: 14, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', borderBottom: '1px solid #0f3460', textAlign: 'left', background: '#16213e' },
    td: { padding: '10px 16px', fontSize: 15, borderBottom: '1px solid #1e2a4a', color: '#d1d5db' },
    badge: (s) => ({ ...statusColor(s), display: 'inline-block', padding: '3px 10px', borderRadius: 20, fontSize: 15, fontWeight: 700 }),
    pageBtn: (active) => ({ padding: '6px 12px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 600, background: active ? '#2563eb' : '#16213e', color: active ? '#fff' : '#9ca3af' }),
  };

  if (!bid) return <div style={{ ...S.page, padding: 24 }}><p style={{ color: '#9ca3af' }}>Please select a business first.</p></div>;

  return (
    <div style={S.page}>
      {/* Top Nav */}
      <div style={S.topBar}>
        {['Dashboard', 'Inbox', 'Contacts', 'Campaign', 'Automation', 'Message Logs', 'Settings'].map(n => (
          <span key={n} style={{ ...S.navLink, color: n === 'Message Logs' ? '#25D366' : '#9ca3af' }}>{n}</span>
        ))}
      </div>

      <div style={S.title}>Message Logs</div>

      {/* Filters */}
      <div style={S.filterRow}>
        <label style={S.label}>Status:</label>
        <select style={S.select} value={status} onChange={e => setStatus(e.target.value)}>
          {['All', 'Sent', 'Delivered', 'Read', 'Failed'].map(s => <option key={s}>{s}</option>)}
        </select>

        <label style={S.label}>Type:</label>
        <select style={S.select} value={type} onChange={e => setType(e.target.value)}>
          {['All', 'Text', 'Template', 'Image', 'Document'].map(t => <option key={t}>{t}</option>)}
        </select>

        <label style={S.label}>Phone:</label>
        <input style={S.input} value={phone} onChange={e => setPhone(e.target.value)} placeholder="Search phone..." />

        <button style={S.filterBtn} onClick={() => { setStatus('All'); setType('All'); setPhone(''); }}>
          Clear
        </button>
      </div>

      {/* Table */}
      <div style={{ overflowX: 'auto', padding: '0 24px' }}>
        <table style={S.table}>
          <thead>
            <tr>
              {['ID', 'Phone', 'Message', 'Type', 'Status', 'Time'].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', padding: 32 }}>Loading...</td></tr>
            ) : paginated.length === 0 ? (
              <tr><td colSpan={6} style={{ ...S.td, textAlign: 'center', padding: 32, color: '#6b7280' }}>No logs found</td></tr>
            ) : paginated.map((m, i) => (
              <tr key={i}>
                <td style={S.td}>{m.id || ((page - 1) * perPage + i + 1)}</td>
                <td style={S.td}>{m.phone}</td>
                <td style={{ ...S.td, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.template_name || m.message || '—'}
                </td>
                <td style={S.td}>{m.type || 'text'}</td>
                <td style={S.td}><span style={S.badge(m.status)}>{m.status}</span></td>
                <td style={S.td}>{m.created_at ? new Date(m.created_at).toLocaleString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{ display: 'flex', gap: 8, padding: '16px 24px', justifyContent: 'center', flexWrap: 'wrap' }}>
          <button style={S.pageBtn(false)} onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
          {Array.from({ length: Math.min(totalPages, 10) }, (_, i) => i + 1).map(p => (
            <button key={p} style={S.pageBtn(p === page)} onClick={() => setPage(p)}>{p}</button>
          ))}
          <button style={S.pageBtn(false)} onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next →</button>
        </div>
      )}
    </div>
  );
}
